import _ from "lodash";
import * as React from "react";

import { useContext } from "react";
import { VictoryAxis } from "victory-axis";
import { VictoryChart } from "victory-chart";
import { VictoryContainer } from "victory-core";

import { TemperatureUnitContext } from "../../../context/app-context";
import { useElementSize } from "../../../hooks/useElementSize";
import { useLoc } from "../../../hooks/useLoc";
import { measures, monthSlugs } from "../../../services/centralStore";
import { WeatherData } from "../../../templates/fragments/weather-data";
import { ChartForm } from "./form";
import { renderBar, renderLine } from "./series";
import {
    DataForChart,
    calculateAxisLabels,
    calculateDomains,
    calculateTickValues,
    convertToDomain,
    defaultChartLabelStyle as defaultLabelStyle,
    fixLabelOverlaps,
    getChartHeight,
    getDomainMapping,
    prepareDataForChart,
    updateMeasuresFromSelector,
    chartWidthBreakpoints as widthBreakpoints
} from "./service";

export const Chart = ({
    weatherData,
    fixedMeasures,
    title,
    className
}: {
    weatherData: WeatherData;
    fixedMeasures?: string[];
    title?: string;
    className?: string;
}) => {
    const currentMeasures = fixedMeasures || measures;
    const loc = useLoc();
    const singleMeasure =
        fixedMeasures && fixedMeasures.length == 1 ? fixedMeasures[0] : null;

    let fixedShownMeasuresState: { [key: string]: boolean } = null;
    if (fixedMeasures) {
        fixedShownMeasuresState = {};
        for (let measure of fixedMeasures) {
            fixedShownMeasuresState[measure] = true;
        }
    }
    // states
    const [shownMeasuresState, setShownMeasuresState] = React.useState(
        fixedShownMeasuresState ||
            updateMeasuresFromSelector({
                selectedValue: "overview",
                weatherData
            })
    );
    const [selectedUnit] = useContext(TemperatureUnitContext);

    // refs
    const chartWrapperRef = React.useRef(null);

    // resizing
    const chartWidthOriginal = useElementSize({
        ref: chartWrapperRef,
        ignoreOffset: true
    });
    const chartWidth = chartWidthOriginal ? chartWidthOriginal - 24 : 600;
    const windowWidth = useElementSize({ defaultValue: 600 });
    // calculations
    const domains = calculateDomains({
        weatherData,
        selectedUnit,
        fixedMeasures
    });
    const domainMapping = getDomainMapping({
        domains,
        selectedUnit,
        singleMeasure
    });
    const measuresShown: string[] = [];
    const chartData: { [key: string]: DataForChart[] } = {};
    if (
        !singleMeasure &&
        windowWidth <= widthBreakpoints.globalWindowBreakpoint
    ) {
        const newMeasuresState = updateMeasuresFromSelector({
            selectedValue: "overview",
            weatherData
        });
        for (let key in shownMeasuresState) {
            shownMeasuresState[key] = newMeasuresState[key];
        }
    }
    for (let measure of currentMeasures) {
        chartData[measure] = prepareDataForChart({
            measure,
            weatherData,
            domainMapping,
            domains,
            selectedUnit
        });
        if (shownMeasuresState[measure]) {
            measuresShown.push(measure);
        }
    }

    fixLabelOverlaps({
        chartData,
        measuresShown,
        currentMeasures,
        domains,
        chartWidth
    });
    let domainsMapping = getDomainMapping({
        domains,
        selectedUnit,
        singleMeasure
    });
    let domainsToShow = domains.map((domain, idx) =>
        _.intersection(domainsMapping[idx], measuresShown).length
            ? domain
            : null
    );
    // in case there is no first domain, third domain take its place
    let firstDomain = domainsToShow[0];
    let firstDomainSwitch = false;
    if (!firstDomain && domainsToShow.length > 2 && domainsToShow[2]) {
        firstDomainSwitch = true;
        firstDomain = domainsToShow[2];
    }
    let secondDomain = domainsToShow[1];
    let thirdDomain =
        domainsToShow.length > 2 && !firstDomainSwitch
            ? domainsToShow[2]
            : null;

    const monthsToShow =
        chartWidth < widthBreakpoints.oddValues
            ? monthSlugs.slice(0, 12).filter((m, idx) => idx % 2 == 0)
            : monthSlugs.slice(0, 12);

    const axisLabels = calculateAxisLabels({
        loc,
        domainMapping,
        selectedUnit,
        measuresShown
    });

    const leftOffsets =
        chartWidth > widthBreakpoints.fullWidthChart
            ? [90, 60, 26]
            : [80, 52, 28];
    const rightOffsets =
        chartWidth > widthBreakpoints.smallerScale ? [56, 26] : [48, 18];
    const axisFontSize = chartWidth > widthBreakpoints.smallerScale ? 15 : 13;

    // render
    return (
        <div
            className={`w-12/12 relative m-auto my-8 max-w-[1200px] select-none px-3 pb-6 shadow-[0_0_30px_rgba(0,0,0,0.25)] sm:w-11/12 md:w-10/12 ${
                title ? "pt-9" : ""
            } ${className}`}
            ref={chartWrapperRef}
        >
            {title && (
                <div className="absolute top-5 left-0 right-0 m-auto text-center text-xl">
                    {title}
                </div>
            )}

            {/* CHART */}
            <div className="">
                {!chartWidthOriginal ? (
                    <div className="chart-fallback"></div>
                ) : (
                    <VictoryChart
                        width={chartWidth}
                        height={getChartHeight(chartWidthOriginal)}
                        padding={{
                            top: 20,
                            bottom: 40,
                            left:
                                firstDomain && thirdDomain
                                    ? leftOffsets[0]
                                    : firstDomain
                                    ? leftOffsets[1]
                                    : leftOffsets[2],
                            right: secondDomain
                                ? rightOffsets[0]
                                : rightOffsets[1]
                        }}
                        domainPadding={{ y: [0, 0] }}
                        style={{}}
                        containerComponent={
                            <VictoryContainer
                                style={{
                                    pointerEvents: "auto",
                                    userSelect: "auto",
                                    touchAction: "auto"
                                }}
                            />
                        }
                    >
                        <VictoryAxis
                            domain={[0.5, 12.5]}
                            offsetY={40}
                            style={{
                                axis: { stroke: "#888" },
                                grid: {
                                    stroke: "#efefef",
                                    strokeDasharray: "5,5"
                                },
                                tickLabels: {
                                    ...defaultLabelStyle,
                                    fontSize: axisFontSize
                                },
                                axisLabel: { ...defaultLabelStyle }
                            }}
                            tickValues={
                                monthsToShow.length > 6
                                    ? [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
                                    : [1, 3, 5, 7, 9, 11]
                            }
                            tickFormat={monthsToShow.map(
                                (m) =>
                                    `${m[0].toUpperCase()}${
                                        chartWidth >
                                        widthBreakpoints.showMonthAbbrev
                                            ? m.slice(1)
                                            : m.slice(1, 3)
                                    }`
                            )}
                        />
                        {domains[0][0] < 0 && (
                            <VictoryAxis
                                domain={[0.5, 12.5]}
                                style={{
                                    grid: { strokeOpacity: 0 },
                                    tickLabels: { opacity: 0 }
                                }}
                                tickValues={[]}
                            />
                        )}
                        {firstDomain && (
                            <VictoryAxis
                                dependentAxis
                                domain={domains[0]}
                                label={axisLabels[firstDomainSwitch ? 2 : 0]}
                                tickValues={
                                    firstDomainSwitch
                                        ? [domains[0][0], domains[0][1]]
                                        : calculateTickValues({ domains })
                                }
                                tickFormat={(val) =>
                                    firstDomainSwitch
                                        ? convertToDomain({
                                              val,
                                              domains,
                                              domain: domains[2],
                                              switched: true
                                          })
                                        : val
                                }
                                style={{
                                    grid: {
                                        stroke: "#efefef",
                                        strokeDasharray: "5,5"
                                    },
                                    tickLabels: {
                                        ...defaultLabelStyle,
                                        fontSize: axisFontSize
                                    },
                                    axisLabel: {
                                        padding: 36,
                                        ...defaultLabelStyle,
                                        fontSize: axisFontSize
                                    }
                                }}
                            />
                        )}
                        {secondDomain && (
                            <VictoryAxis
                                dependentAxis
                                orientation={`right`}
                                label={axisLabels[1]}
                                domain={domains[0]}
                                tickValues={[domains[0][0], domains[0][1]]}
                                tickFormat={(val) =>
                                    convertToDomain({
                                        val,
                                        domains,
                                        domain: domains[1],
                                        switched: true
                                    })
                                }
                                style={{
                                    grid: { strokeOpacity: 0 },
                                    tickLabels: {
                                        ...defaultLabelStyle,
                                        fontSize: axisFontSize
                                    },
                                    axisLabel: {
                                        padding: 14,
                                        ...defaultLabelStyle,
                                        fontSize: axisFontSize
                                    }
                                }}
                            />
                        )}
                        {(!firstDomain || !secondDomain) && (
                            <VictoryAxis
                                dependentAxis
                                orientation={firstDomain ? `right` : `left`}
                                tickValues={[]}
                                style={{
                                    grid: { strokeOpacity: 0 },
                                    tickLabels: { opacity: 0 }
                                }}
                            />
                        )}

                        {thirdDomain && (
                            <VictoryAxis
                                offsetX={46}
                                dependentAxis
                                domain={domains[0]}
                                label={axisLabels[2]}
                                tickValues={[domains[0][0], domains[0][1]]}
                                tickFormat={(val) =>
                                    convertToDomain({
                                        val,
                                        domains,
                                        domain: domains[2],
                                        switched: true
                                    })
                                }
                                style={{
                                    axis: { strokeOpacity: 0 },
                                    grid: { strokeOpacity: 0 },
                                    axisLabel: {
                                        ...defaultLabelStyle,
                                        opacity: 0.5,
                                        fontSize: axisFontSize
                                    },
                                    tickLabels: {
                                        ...defaultLabelStyle,
                                        opacity: 0.5,
                                        fontSize: axisFontSize
                                    }
                                }}
                            />
                        )}
                        {shownMeasuresState["ppt"] &&
                            renderBar({
                                measure: "ppt",
                                chartWidth,
                                chartData
                            })}
                        {shownMeasuresState["tmax"] &&
                            renderLine({
                                measure: "tmax",
                                chartWidth,
                                chartData
                            })}
                        {shownMeasuresState["tmin"] &&
                            renderLine({
                                measure: "tmin",
                                chartWidth,
                                chartData
                            })}
                        {shownMeasuresState["sst"] &&
                            renderLine({
                                measure: "sst",
                                chartWidth,
                                chartData
                            })}
                        {shownMeasuresState["wet"] &&
                            renderLine({
                                measure: "wet",
                                chartWidth,
                                chartData
                            })}
                        {shownMeasuresState["sunhrs"] &&
                            renderLine({
                                measure: "sunhrs",
                                chartWidth,
                                chartData
                            })}
                        {shownMeasuresState["rhm"] &&
                            renderLine({
                                measure: "rhm",
                                chartWidth,
                                chartData
                            })}
                        {shownMeasuresState["ws"] &&
                            renderLine({
                                measure: "ws",
                                chartWidth,
                                chartData
                            })}
                    </VictoryChart>
                )}
            </div>
            <ChartForm
                shownMeasuresState={shownMeasuresState}
                setShownMeasuresState={setShownMeasuresState}
                weatherData={weatherData}
                currentMeasures={currentMeasures}
                allowMeasureSelection={true}
                className="mdmax:hidden"
            />
            <ChartForm
                shownMeasuresState={shownMeasuresState}
                setShownMeasuresState={setShownMeasuresState}
                weatherData={weatherData}
                currentMeasures={currentMeasures}
                allowMeasureSelection={false}
                className="md:hidden"
            />
        </div>
    );
};
